import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    GripVertical,
    Image,
    Package,
    Grid,
    MessageSquare,
    Megaphone,
    Layers,
    X,
    Save,
    Eye,
    EyeOff,
} from 'lucide-react';
import Notification from '../Common/Notification';
import { storefrontGet, storefrontPost, storefrontPut, storefrontDelete } from '../../services/storefrontApi';
import AppleSelect from '../Common/AppleSelect';
import ActionMenu from '../Common/ActionMenu';

const SECTION_TYPES = [
    { key: 'hero',         label: 'Hero (bannière principale)', icon: Image },
    { key: 'products',     label: 'Produits',                   icon: Package },
    { key: 'categories',   label: 'Catégories',                 icon: Grid },
    { key: 'testimonials', label: 'Témoignages',                icon: MessageSquare },
    { key: 'cta',          label: 'Appel à l\'action',          icon: Megaphone },
    { key: 'custom',       label: 'Personnalisé',               icon: Layers },
];

const TYPE_ICONS = Object.fromEntries(SECTION_TYPES.map(t => [t.key, t.icon]));

/* ─── Section editor ────────────────────────────────────────────────────── */
const SectionEditor = ({ section, onClose, onSave, isSaving }) => {
    const isEdit = !!section?.id;
    const [form, setForm] = useState({
        type:       section?.type       || 'hero',
        title:      section?.title      || '',
        subtitle:   section?.subtitle   || '',
        position:   section?.position   ?? 0,
        active:     section?.active     ?? true,
        full_width: section?.full_width ?? false,
        padding:    section?.padding    || 'md',
        config:     section?.config     || {},
        style:      section?.style      || {},
    });

    const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setCfg = (k, v) => setForm(f => ({ ...f, config: { ...f.config, [k]: v } }));
    const setSty = (k, v) => setForm(f => ({ ...f, style: { ...f.style, [k]: v } }));

    const sectionType = SECTION_TYPES.find(t => t.key === form.type);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {isEdit ? 'Modifier la section' : 'Nouvelle section'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-5 space-y-4 max-h-[78vh] overflow-y-auto">
                    {/* Type */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Type de section</label>
                        <AppleSelect
                            value={form.type}
                            onChange={e => set('type', e.target.value)}
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                        >
                            {SECTION_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </AppleSelect>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Titre</label>
                            <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
                                placeholder="Bienvenue chez nous"
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Position</label>
                            <input type="number" min={0} value={form.position} onChange={e => set('position', parseInt(e.target.value, 10) || 0)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Sous-titre</label>
                        <input type="text" value={form.subtitle} onChange={e => set('subtitle', e.target.value)}
                            placeholder="Votre sous-titre..."
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                    </div>

                    {/* Config par type */}
                    {form.type === 'hero' && (
                        <div className="space-y-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Configuration Hero</p>
                            <input type="url" value={form.config.image || ''} onChange={e => setCfg('image', e.target.value)}
                                placeholder="URL image hero"
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                            <div className="grid grid-cols-2 gap-3">
                                <input type="text" value={form.config.cta_text || ''} onChange={e => setCfg('cta_text', e.target.value)}
                                    placeholder="Texte du bouton"
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                                <input type="text" value={form.config.cta_url || ''} onChange={e => setCfg('cta_url', e.target.value)}
                                    placeholder="URL du bouton"
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                            </div>
                        </div>
                    )}

                    {form.type === 'products' && (
                        <div className="space-y-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Configuration Produits</p>
                            <AppleSelect value={form.config.source || 'best_sellers'} onChange={e => setCfg('source', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary">
                                <option value="best_sellers">Best Sellers</option>
                                <option value="featured">À la une</option>
                                <option value="new">Nouveautés</option>
                                <option value="random">Aléatoire</option>
                            </AppleSelect>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Nombre</label>
                                    <input type="number" min={2} max={20} value={form.config.limit || 8} onChange={e => setCfg('limit', parseInt(e.target.value, 10))}
                                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Colonnes</label>
                                    <AppleSelect value={form.config.columns || 4} onChange={e => setCfg('columns', parseInt(e.target.value, 10))}
                                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary">
                                        {[2, 3, 4, 5, 6].map(n => <option key={n}>{n}</option>)}
                                    </AppleSelect>
                                </div>
                            </div>
                        </div>
                    )}

                    {form.type === 'categories' && (
                        <div className="space-y-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Configuration Catégories</p>
                            <AppleSelect value={form.config.layout || 'grid'} onChange={e => setCfg('layout', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary">
                                <option value="grid">Grille</option>
                                <option value="list">Liste</option>
                                <option value="carousel">Carrousel</option>
                            </AppleSelect>
                        </div>
                    )}

                    {form.type === 'cta' && (
                        <div className="space-y-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Configuration CTA</p>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="text" value={form.config.button_text || ''} onChange={e => setCfg('button_text', e.target.value)}
                                    placeholder="Voir le menu"
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                                <input type="text" value={form.config.button_url || ''} onChange={e => setCfg('button_url', e.target.value)}
                                    placeholder="/products"
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                            </div>
                        </div>
                    )}

                    {/* Style */}
                    <div className="grid grid-cols-2 gap-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                        <p className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Style</p>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Couleur de fond</label>
                            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 bg-white dark:bg-gray-900">
                                <input type="color" value={form.style.background_color || '#FFFFFF'} onChange={e => setSty('background_color', e.target.value)}
                                    className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{form.style.background_color || '#FFFFFF'}</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Couleur du texte</label>
                            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 bg-white dark:bg-gray-900">
                                <input type="color" value={form.style.text_color || '#000000'} onChange={e => setSty('text_color', e.target.value)}
                                    className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{form.style.text_color || '#000000'}</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Padding</label>
                            <AppleSelect value={form.padding} onChange={e => set('padding', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary">
                                {['none', 'sm', 'md', 'lg', 'xl'].map(p => <option key={p}>{p}</option>)}
                            </AppleSelect>
                        </div>
                    </div>

                    <div className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-700 dark:text-gray-200">Pleine largeur</span>
                        <button type="button" onClick={() => set('full_width', !form.full_width)}
                            className={`relative h-6 w-11 rounded-full transition-colors ${form.full_width ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${form.full_width ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-700 dark:text-gray-200">Actif</span>
                        <button type="button" onClick={() => set('active', !form.active)}
                            className={`relative h-6 w-11 rounded-full transition-colors ${form.active ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={isSaving}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Section row ───────────────────────────────────────────────────────── */
const SectionRow = ({ section, onEdit, onDelete, onToggle }) => {
    const Icon = TYPE_ICONS[section.type] || Layers;
    const typeLabel = SECTION_TYPES.find(t => t.key === section.type)?.label || section.type;

    return (
        <div className={`flex items-center gap-3 px-4 py-3 border rounded-xl group transition-opacity ${section.active ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-60'}`}>
            <GripVertical size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0 cursor-grab" />
            <div className="p-1.5 rounded-lg bg-primary/10">
                <Icon size={15} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{section.title || '(Sans titre)'}</p>
                <p className="text-xs text-gray-400">{typeLabel} · position {section.position ?? 0}</p>
            </div>
            <ActionMenu
                label={`Actions pour ${section.title || ''}`}
                items={[
                    {label: section.active ? 'Masquer' : 'Afficher', icon: section.active ? EyeOff : Eye, onClick: () => onToggle(section)},
                    {label: 'Modifier', icon: Pencil, onClick: () => onEdit(section)},
                    {divider: true},
                    {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(section)},
                ]}
            />
        </div>
    );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const HomepageBuilder = () => {
    const [sections, setSections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editorSection, setEditorSection] = useState(undefined); // undefined=closed null=new
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type !== 'loading') setTimeout(() => setNotification(n => ({ ...n, show: false })), 3000);
    };

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await storefrontGet('/homepage');
            const data = res.data?.data ?? res.data ?? [];
            setSections(Array.isArray(data) ? data : []);
        } catch {
            showNotification('error', 'Impossible de charger les sections.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (form) => {
        setIsSaving(true);
        try {
            if (editorSection?.id) {
                await storefrontPut(`/homepage/${editorSection.id}`, form);
                showNotification('success', 'Section mise à jour.');
            } else {
                await storefrontPost('/homepage', form);
                showNotification('success', 'Section créée.');
            }
            setEditorSection(undefined);
            load();
        } catch {
            showNotification('error', 'Erreur lors de l\'enregistrement.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (section) => {
        if (!window.confirm(`Supprimer la section « ${section.title || section.type} » ?`)) return;
        try {
            await storefrontDelete(`/homepage/${section.id}`);
            showNotification('success', 'Section supprimée.');
            load();
        } catch {
            showNotification('error', 'Impossible de supprimer cette section.');
        }
    };

    const handleToggle = async (section) => {
        try {
            await storefrontPut(`/homepage/${section.id}`, { active: !section.active });
            setSections(prev => prev.map(s => s.id === section.id ? { ...s, active: !s.active } : s));
        } catch {
            showNotification('error', 'Erreur lors de la mise à jour.');
        }
    };

    const sorted = [...sections].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return (
        <div className="px-4 py-6 max-w-3xl mx-auto">
            <Notification show={notification.show} type={notification.type} message={notification.message} onClose={() => setNotification(n => ({ ...n, show: false }))} />

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-base font-semibold text-gray-800 dark:text-white">Sections de la page d'accueil</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Organisez et personnalisez les blocs affichés sur votre boutique.</p>
                </div>
                <button
                    onClick={() => setEditorSection(null)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors"
                >
                    <Plus size={16} /> Ajouter une section
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <RefreshCw size={20} className="animate-spin text-primary" />
                </div>
            ) : sections.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <Layers size={40} className="mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Aucune section configurée.</p>
                    <button onClick={() => setEditorSection(null)} className="mt-3 text-primary text-sm hover:underline">
                        Créer la première section
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {sorted.map(s => (
                        <SectionRow
                            key={s.id}
                            section={s}
                            onEdit={setEditorSection}
                            onDelete={handleDelete}
                            onToggle={handleToggle}
                        />
                    ))}
                </div>
            )}

            {editorSection !== undefined && (
                <SectionEditor
                    section={editorSection}
                    onClose={() => setEditorSection(undefined)}
                    onSave={handleSave}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default HomepageBuilder;
